import React, { useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Divider,
  IconButton,
  useTheme,
  TextField,
  Switch,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import DatePicker2 from "../../../../../../views/components/common/textFields/DatePicker2";
import dayjs, { Dayjs } from "dayjs";
import { UnpaidSamplesReturnInfo } from "../../../../../../models/ProjectManagement/UnpaidSample";
import { useStores } from "../../../../use-stores";
import MediaAssetsUpload from "../../../../../../views/components/common/upload/MediaAssetsUpload";
import ImageAPI from "../../../../api/ImageAPI";
import FileUploadIcon from "../../../../../../assets/fileuploadicon.png";

interface UnpaidsamplePickupPopupProps {
  pickUpdata: UnpaidSamplesReturnInfo | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: UnpaidSamplesReturnInfo) => Promise<void>;
  order_id: string;
}

const UnpaidsamplePickupPopup: React.FC<UnpaidsamplePickupPopupProps> = ({
  open,
  onClose,
  onSave,
  pickUpdata,
  order_id,
}) => {
  const { projectManagementStore } = useStores();
  const theme = useTheme();
  const selectedFiles = projectManagementStore.unpaidSamplePickupSelectedFiles;
  const receivedAtOffice =
    projectManagementStore.unpaidSamplePickupReceivedAtOffice;

  useEffect(() => {
    if (open) {
      if (
        pickUpdata &&
        (pickUpdata.id || pickUpdata.order_id) &&
        (pickUpdata.client_contacted !== undefined ||
          pickUpdata.pickup_confirmed !== undefined)
      ) {
        projectManagementStore.updateUnpaidSamplePickupFormData({
          client_contacted: Boolean(pickUpdata.client_contacted),
          pickup_confirmed: Boolean(pickUpdata.pickup_confirmed),
          pickup_date: pickUpdata.pickup_date
            ? dayjs(pickUpdata.pickup_date).format("YYYY-MM-DD HH:mm:ss")
            : "",
          remarks: pickUpdata.remarks || "",
          sample_received_at_office: Boolean(pickUpdata.sample_received_at_office),
          image_urls: pickUpdata.image_urls || [],
        });
        projectManagementStore.setUnpaidSamplePickupReceivedAtOffice(
          Boolean(pickUpdata.sample_received_at_office)
        );
      } else {
        projectManagementStore.resetUnpaidSamplePickupFormData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pickUpdata]);

  const handleClientContactedChange = (contacted: boolean) => {
    projectManagementStore.updateUnpaidSamplePickupFormData({
      client_contacted: contacted,
    });
  };

  const handlePickupConfirmedChange = (confirmed: boolean) => {
    projectManagementStore.updateUnpaidSamplePickupFormData({
      pickup_confirmed: confirmed,
    });
  };

  const handleRemarksChange = (value: string) => {
    projectManagementStore.updateUnpaidSamplePickupFormData({ remarks: value });
  };

  const handlePickupDateChange = (date: Dayjs | null) => {
    projectManagementStore.updateUnpaidSamplePickupFormData({
      pickup_date: date ? date.format("YYYY-MM-DD HH:mm:ss") : undefined,
    });
  };

  const handleReceivedAtOfficeChange = (received: boolean) => {
    projectManagementStore.setUnpaidSamplePickupReceivedAtOffice(received);
  };

  const handleImageUpload = (files: File[]) => {
    projectManagementStore.addUnpaidSamplePickupFiles(files);
  };

  const existingImageList = useMemo(() => {
    const imageUrls =
      projectManagementStore.unpaidSamplePickupFormData.image_urls ||
      pickUpdata?.image_urls ||
      [];
    if (Array.isArray(imageUrls)) {
      return imageUrls.map((url, index) => ({
        id: url.id,
        title: `Image ${index + 1}`,
        img: url.image_url,
      }));
    }
    return [];
  }, [
    projectManagementStore.unpaidSamplePickupFormData.image_urls,
    pickUpdata?.image_urls,
  ]);

  const handleRemoveLocalImage = (image: {
    id: string | number;
    title?: string;
    img: string;
  }) => {
    if (typeof image.id === "string" && image.id.startsWith("file-")) {
      const fileIndex = parseInt(image.id.replace("file-", ""), 10);
      if (image.img.startsWith("blob:")) {
        URL.revokeObjectURL(image.img);
      }
      projectManagementStore.removeUnpaidSamplePickupFileAtIndex(fileIndex);
    }
  };

  const isFormValid = () => {
    const formData = projectManagementStore.unpaidSamplePickupFormData;

    if (
      formData.client_contacted === undefined ||
      formData.pickup_confirmed === undefined
    ) {
      return false;
    }

    if (formData.pickup_confirmed === false && !formData.remarks?.trim()) {
      return false;
    }

    if (formData.pickup_confirmed === true && !formData.pickup_date) {
      return false;
    }

    if (receivedAtOffice && selectedFiles.length === 0) {
      return false;
    }

    if (receivedAtOffice && !formData.remarks?.trim()) {
      return false;
    }

    return true;
  };

  const canSave = isFormValid();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        transition: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        backdrop: {
          timeout: 300,
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "90vh",
            maxWidth: {
              xs: "90vw",
              sm: "70vw",
              md: "60vw",
              lg: "50vw",
              xl: "30vw",
            },
            width: {
              xs: "90vw",
              sm: "70vw",
              md: "60vw",
              lg: "50vw",
              xl: "30vw",
            },
            margin: { xs: "16px", sm: "32px" },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{
        zIndex: 2000,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <CustomTypography variant="heading3">
            {`Unpaid Sample Action  (${order_id})`}
          </CustomTypography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.palette.customColors.greyTextColor,
              "&:hover": {
                backgroundColor: theme.palette.customColors.lightGreyColor,
                color: theme.palette.customColors.blackBorder,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            padding: { xs: "20px", sm: "24px" },
            flex: 1,
            overflow: "auto",
            maxHeight: "60vh",
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.customColors.greyBackground,
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.customColors.greyDarkborder,
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: theme.palette.customColors.greyTextColor,
              },
            },
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: 0, lg: 4 },
                alignItems: { lg: "flex-start" },
              }}
            >
              <Box sx={{ flex: 1, mb: { xs: 2, lg: 0 } }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Client Contacted
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  Whether the client has been contacted regarding the pickup of
                  samples
                </CustomTypography>

                <Box sx={{ flex: 1 }}>
                  <RadioGroup
                    row
                    value={String(
                      projectManagementStore.unpaidSamplePickupFormData
                        .client_contacted
                    )}
                    onChange={(e) =>
                      handleClientContactedChange(e.target.value === "true")
                    }
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontWeight: 500,
                        fontSize: "0.9rem",
                      },
                      "& .MuiRadio-root": {
                        padding: "4px",
                        marginLeft: "4px",
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.1rem",
                        },
                      },
                    }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio size="small" />}
                      label="No"
                    />
                  </RadioGroup>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: { xs: 0, lg: 4 },
                alignItems: { lg: "flex-start" },
              }}
            >
              <Box sx={{ flex: 1, mb: { xs: 2, lg: 0 } }}>
                <CustomTypography
                  variant="heading6"
                  style={{ fontWeight: 600, marginBottom: 6 }}
                >
                  Pickup Confirmation
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  style={{
                    marginBottom: 20,
                    lineHeight: "1.2",
                    color: theme.palette.text.secondary,
                  }}
                >
                  Confirmation from the client regarding pickup of samples
                </CustomTypography>

                <Box sx={{ flex: 1 }}>
                  <RadioGroup
                    row
                    value={String(
                      projectManagementStore.unpaidSamplePickupFormData
                        .pickup_confirmed
                    )}
                    onChange={(e) =>
                      handlePickupConfirmedChange(e.target.value === "true")
                    }
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontWeight: 500,
                        fontSize: "0.9rem",
                      },
                      "& .MuiRadio-root": {
                        padding: "4px",
                        marginLeft: "4px",
                        "& .MuiSvgIcon-root": {
                          fontSize: "1.1rem",
                        },
                      },
                    }}
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" />}
                      label="Yes"
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio size="small" />}
                      label="No"
                    />
                  </RadioGroup>
                </Box>
              </Box>

              {projectManagementStore.unpaidSamplePickupFormData
                .pickup_confirmed === true && (
                  <Box sx={{ flex: 1 }}>
                    <CustomTypography
                      variant="heading6"
                      style={{ fontWeight: 600, marginBottom: 6 }}
                    >
                      Pickup Date
                      <span
                        style={{ color: theme.palette.error.main, marginLeft: 4 }}
                      >
                        *
                      </span>
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      style={{
                        marginBottom: 20,
                        lineHeight: "1.2",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Select the pickup date for the samples
                    </CustomTypography>

                    <Box sx={{ position: "relative" }}>
                      <DatePicker2
                        type="date"
                        label="Pickup Date"
                        value={
                          projectManagementStore.unpaidSamplePickupFormData
                            .pickup_date
                            ? dayjs(
                              projectManagementStore.unpaidSamplePickupFormData
                                .pickup_date
                            )
                            : null
                        }
                        onChange={handlePickupDateChange}
                        zIndex={4000}
                        minDate={dayjs(new Date())}
                      />
                    </Box>
                  </Box>
                )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 3,
            }}
          >
            <CustomTypography
              variant="heading6"
              style={{ fontWeight: 600, marginBottom: 0 }}
            >
              Sample Received at Office
            </CustomTypography>
            <FormControlLabel
              control={
                <Switch
                  checked={receivedAtOffice}
                  onChange={(e) =>
                    handleReceivedAtOfficeChange(e.target.checked)
                  }
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{ "aria-label": "Sample received at office" }}
                />
              }
              label=""
              sx={{ marginRight: 0 }}
            />
          </Box>

          {receivedAtOffice && (
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="heading6"
                style={{ fontWeight: 600, marginBottom: 6 }}
              >
                Upload Images                   <span
                  style={{ color: theme.palette.error.main, marginLeft: 4 }}
                >
                  *
                </span>
              </CustomTypography>
              <CustomTypography
                variant="body2"
                style={{
                  marginBottom: 20,
                  lineHeight: "1.2",
                  color: theme.palette.text.secondary,
                }}
              >
                Upload images related to the received samples
              </CustomTypography>
              <Box sx={{ width: "100%", maxWidth: "100%" }}>
                <MediaAssetsUpload
                  hideDeleteForExistingImages={true}
                  uploadIcon={
                    <img
                      src={FileUploadIcon}
                      alt="Upload"
                      width={40}
                      height={40}
                    />
                  }
                  fileInfo="PNG, JPG up to 2MB each"
                  multiple={true}
                  existingImageList={existingImageList}
                  newImageList={selectedFiles}
                  removeImage={handleRemoveLocalImage}
                  addImagesToBeUploaded={handleImageUpload}
                />
              </Box>
            </Box>
          )}

          <Box sx={{ mb: 3 }}>
            <CustomTypography
              variant="heading6"
              style={{ fontWeight: 600, marginBottom: 6 }}
            >
              Remarks
              {(projectManagementStore.unpaidSamplePickupFormData
                .pickup_confirmed === false ||
                receivedAtOffice) && (
                  <span
                    style={{ color: theme.palette.error.main, marginLeft: 4 }}
                  >
                    *
                  </span>
                )}
            </CustomTypography>
            <CustomTypography
              variant="body2"
              style={{
                marginBottom: 20,
                lineHeight: "1.2",
                color: theme.palette.text.secondary,
              }}
            >
              Any additional remarks or comments
            </CustomTypography>

            <TextField
              multiline={true}
              rows={3}
              fullWidth
              placeholder="Enter any remarks or additional information..."
              value={
                projectManagementStore.unpaidSamplePickupFormData.remarks || ""
              }
              onChange={(e) => handleRemarksChange(e.target.value)}
              required={
                projectManagementStore.unpaidSamplePickupFormData
                  .pickup_confirmed === false || receivedAtOffice
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: theme.palette.customColors.greyDarkborder,
                  },
                  "&:hover fieldset": {
                    borderColor: theme.palette.customColors.blueGradientStart,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: theme.palette.customColors.blueGradientStart,
                    borderWidth: "1px",
                  },
                },
              }}
            />
          </Box>
          <Divider sx={{ my: 3 }} />
        </DialogContent>

        <DialogActions
          sx={{
            padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
            borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
            backgroundColor: theme.palette.customColors.lightGreyColor,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            {pickUpdata && (
              <>
                <CustomTypography variant="body3">
                  <span style={{ fontWeight: 600 }}>Created by : </span>
                  {pickUpdata?.sales_poc_name || " Not yet saved"}
                </CustomTypography>
                <CustomTypography variant="body3">
                  <span style={{ fontWeight: 600 }}>Last Updated by : </span>
                  {pickUpdata?.modified_by_name || "Not yet updated"}
                </CustomTypography>
                {/* <CustomTypography variant="body3" style={{ marginBottom: 5 }}>
                  <span style={{ fontWeight: 600 }}>Last Updated on : </span>
                  {pickUpdata?.modified_date
                    ? dayjs(pickUpdata.modified_date).format("DD/MM/YYYY HH:mm")
                    : "Not yet updated"}
                </CustomTypography> */}
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                borderColor: theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.greyTextColor,
                fontWeight: 600,
                padding: "10px 24px",
                borderRadius: "8px",
                "&:hover": {
                  borderColor: theme.palette.customColors.greyTextColor,
                  backgroundColor: theme.palette.customColors.lightGreyColor,
                },
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={async () => {
                const formData =
                  projectManagementStore.unpaidSamplePickupFormData;
                let uploadedImageUrls: string[] = [];
                if (
                  receivedAtOffice &&
                  selectedFiles.length > 0
                ) {
                  const imageAPI = new ImageAPI();
                  uploadedImageUrls = await imageAPI.uploadImage(
                    selectedFiles,
                    "product-images"
                  );
                  projectManagementStore.setUnpaidSamplePickupSelectedFiles([]);
                }
                const payload: UnpaidSamplesReturnInfo = {
                  client_contacted: formData.client_contacted,
                  pickup_confirmed: formData.pickup_confirmed,
                  pickup_date: formData.pickup_date,
                  remarks: formData.remarks,
                  sample_received_at_office: receivedAtOffice,
                  ...(receivedAtOffice && uploadedImageUrls.length > 0
                    ? { image_url: uploadedImageUrls }
                    : {}),
                };
                await onSave(payload);
              }}
              variant="contained"
              disabled={!canSave}
              sx={{
                backgroundColor: canSave
                  ? theme.palette.customColors.EmeraldGreen
                  : theme.palette.customColors.greyDarkborder,
                color: theme.palette.customColors.white,
                fontWeight: 600,
                padding: "10px 24px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: canSave
                    ? theme.palette.customColors.greenDark
                    : theme.palette.customColors.greyDarkborder,
                  transform: canSave ? "translateY(-1px)" : "none",
                  boxShadow: canSave
                    ? `0 4px 12px ${theme.palette.customColors.EmeraldGreen}30`
                    : "none",
                },
                "&:disabled": {
                  backgroundColor: theme.palette.customColors.greyDarkborder,
                  color: theme.palette.customColors.white,
                },
              }}
            >
              Save
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default observer(UnpaidsamplePickupPopup);
